//Global imports
import { useState } from 'react';
import { cilPencil, cilPlus } from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import {
    CButton,
    CCard,
    CCardBody,
    CCardFooter,
    CCardHeader,
    CCardTitle,
    CCol,
} from '@coreui/react';

//File imports
import { Language } from '../../../../language/English';
import { DocumentCategoryPojo } from '../../Api/DocumentCategoryPojo';
import {
    DOCUMENT_CATEGORY_PAGE_STATUS_ACTIVE,
    DOCUMENT_CATEGORY_PAGE_STATUS_INACTIVE,
} from '../../Api/DocumentCategoryConstants';
import { DOCUMENT_GROUP_PAGES_TRANSLATION } from '../../../../PagesNames';

//Component imports
import ConfirmationBoxModal from '../../../../components/Layout/ConfirmationBoxModal';

const DocumentCategoryCard = (props: {
    documentCategory: DocumentCategoryPojo;
    key: number;
    deleteDocumentCategory: () => void;
    changeDocumentCategoryStatus: (status: number) => void;
    onEdit: (category: DocumentCategoryPojo) => void;
    navigate: any;
}) => {
    const data = props.documentCategory;
    const [deleteConfirmation, setDeleteConfirmation] =
        useState<boolean>(false);
    const [noTranslationsAvailable, setNoTranslationsAvailable] =
        useState<boolean>(true);
    const [availableTranslations, setAvailableTranslations] = useState<
        string[]
    >([]);

    const closeDeleteConfirmation = () => setDeleteConfirmation(false);

    const openDeleteConfirmation = () => getExistsTranslationNameInString();

    const _deleteDocumentCategory = () => {
        closeDeleteConfirmation();
        props.deleteDocumentCategory();
    };

    const changeDocumentCategoryStatus = () => {
        closeDeleteConfirmation();
        if (data.status === 1) {
            props.changeDocumentCategoryStatus(
                DOCUMENT_CATEGORY_PAGE_STATUS_INACTIVE,
            );
        } else {
            props.changeDocumentCategoryStatus(
                DOCUMENT_CATEGORY_PAGE_STATUS_ACTIVE,
            );
        }
    };

    const handleLanguageClick = (language: string, status: number) => {
        props.navigate(DOCUMENT_GROUP_PAGES_TRANSLATION, {
            state: {
                language,
                id: data.documentCategoryPageId,
                isEdit: status,
                name: data.name,
            },
        });
    };

    const getExistsTranslationNameInString = () => {
        type LanguageKey = keyof typeof data.translations;
        const activeLanguages = Object.keys(data?.translations).filter(
            (lang) => data?.translations[lang as LanguageKey] === 1,
        );
        if (activeLanguages.length > 0) {
            setNoTranslationsAvailable(false);
            let translations = activeLanguages.map((lang) => {
                switch (lang) {
                    case 'en':
                        return Language.ENGLISH;
                    case 'fr':
                        return Language.FRENCH;
                    case 'de':
                        return Language.GERMAN;
                    default:
                        return '';
                }
            });
            setAvailableTranslations(translations);
        }
        setDeleteConfirmation(true);
    };

    return (
        <CCol xs={12} md={4} className="mb-4">
            <CCard className="border-0 shadow-sm rounded-2">
                <CCardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                        <div
                            className="close-button"
                            onClick={openDeleteConfirmation}
                        >
                            <span>X</span>
                        </div>
                        <div className={`label-position`}>
                            <div
                                className={`${data.status === 1 ? 'article-label' : 'article-label disable'}`}
                            >
                                {data.status === 1
                                    ? Language.ACTIVE
                                    : Language.INACTIVE}
                            </div>
                        </div>
                    </div>
                </CCardHeader>
                <CCardBody>
                    <div className="d-flex justify-content-between align-items-start my-2">
                        <CCardTitle
                            className="fs-3 text-truncate"
                            style={{ maxWidth: '100%' }}
                        >
                            {data.name}
                        </CCardTitle>
                    </div>

                    <div className="d-flex gap-2 language-button mb-1">
                        <CButton
                            color="primary"
                            onClick={() =>
                                handleLanguageClick('en', data.translations.en)
                            }
                        >
                            <CIcon
                                icon={
                                    data.translations.en === 0
                                        ? cilPlus
                                        : cilPencil
                                }
                                className="me-2"
                            />
                            {Language.ENGLISH}
                        </CButton>
                        <CButton
                            color="info"
                            onClick={() =>
                                handleLanguageClick('fr', data.translations.fr)
                            }
                        >
                            <CIcon
                                icon={
                                    data.translations.fr === 0
                                        ? cilPlus
                                        : cilPencil
                                }
                                className="me-2"
                            />
                            {Language.FRENCH}
                        </CButton>
                        <CButton
                            color="warning"
                            onClick={() =>
                                handleLanguageClick('de', data.translations.de)
                            }
                        >
                            <CIcon
                                icon={
                                    data.translations.de === 0
                                        ? cilPlus
                                        : cilPencil
                                }
                                className="me-2"
                            />
                            {Language.GERMAN}
                        </CButton>
                    </div>
                </CCardBody>
                <CCardFooter className="bg-white">
                    <div className="d-flex gap-2">
                        <CButton
                            color="primary"
                            size="sm"
                            onClick={() => props.onEdit(data)}
                        >
                            <CIcon icon={cilPencil} className="me-2" />
                            {Language.EDIT_DOCUMENT_GROUP_PAGE}
                        </CButton>
                        <CButton
                            onClick={changeDocumentCategoryStatus}
                            color={`${data.status === 1 ? 'secondary' : 'success'}`}
                            className="me-2"
                        >
                            {`${data.status === 1 ? Language.INACTIVATE : Language.ACTIVATE}`}
                        </CButton>
                    </div>
                </CCardFooter>
            </CCard>

            {deleteConfirmation && (
                <ConfirmationBoxModal
                    onClose={closeDeleteConfirmation}
                    title={Language.DELETE_DOCUMENT_GROUP_PAGE}
                    message={`${Language.DELETE_CONFIRMATION_WITH_DOCUMENT_NAME} ${data.name} ?`}
                    subMessage={
                        !noTranslationsAvailable
                            ? Language.CATEGORY_DELETE_CONFIRMATION +
                              availableTranslations.join(', ')
                            : ''
                    }
                    buttonOneText={Language.YES}
                    buttonTwoText={Language.CLOSE}
                    buttonOneCallable={_deleteDocumentCategory}
                    buttonTwoCallable={closeDeleteConfirmation}
                    buttonOneCallBackFunctionParams={1}
                    buttonTwoCallBackFunctionParams={null}
                />
            )}
        </CCol>
    );
};

export default DocumentCategoryCard;
